import React, { PropsWithChildren, Ref, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { SxProps } from '@cp/ds';
import { GeoPoint } from '@youhelp-ru/core';
import { styled } from '@cp/ds/src/theme';
import type { Marker, YHMap } from './yh-map';
export type { Marker, YHMap } from './yh-map';

const Root = styled(Box, { name: 'map' })({
  overflow: 'hidden',
});

export interface MapApi {
  setMarkers(markers: Marker[]): void;
  getInstance(): YHMap | null;
}

export interface MapProps extends PropsWithChildren {
  sx?: SxProps;
  onMapCreated?: (mapInstance: HTMLElement) => void;
  center?: GeoPoint | string;
  /** Static single marker */
  marker?: GeoPoint | string;
  zoom?: number;
  zoomControl?: boolean;
  markers?: Marker[];
  ref?: Ref<MapApi>;
}

export const Map = ({ center, sx, zoom, marker, ref: imperativeHandleRef, children, markers, onMapCreated, ...props }: MapProps) => {
  const ref = useRef<YHMap>(null);
  useLayoutEffect(() => {
    import('./yh-map').then((module) => module.YHMap.register());
  }, []);

  useEffect(() => {
    if (!markers || !ref.current) {
      return;
    }
    const mapNode = ref.current;
    const update = () => mapNode.setMarkers(markers);
    if (!mapNode.ready) {
      ref.current.addEventListener('ready', update, { once: true });
    } else {
      update();
    }
    return () => mapNode.removeEventListener('ready', update);
  }, [markers]);

  useImperativeHandle(imperativeHandleRef, () => ({
    getInstance: () => ref.current,
    setMarkers(m: Marker[]) {
      if (!ref.current || ref.current.ready) {
        return false;
      }
      ref.current.markers = m;
      return true;
    },
  }));
  // @ts-expect-error unknown jsx element for custom component
  return React.createElement(Root, { as: 'yh-map', ref, sx, zoom, center, marker, ...props }, children);
};
